import Vue from "vue";

/**
 * Dialog helpers — thin wrappers around Buefy's dialog API that return
 * Promises and pull sensible Omx defaults.
 *
 * Installed as global mixin methods:
 *
 *   $omxConfirm({...})  — destructive/confirmation prompt or optimistic-undo flow
 *   $omxAlert({...})    — informational alert with a single OK button
 *   $omxPrompt({...})   — text-input prompt
 *
 * --- $omxConfirm ---
 *
 *   1. mode: 'modal'   (default for irreversible ops)
 *      Shows a $buefy.dialog.confirm. Resolves true if confirmed.
 *
 *        await this.$omxConfirm({
 *          title: this.$t('projects-view.delete-title'),
 *          message: this.$t('projects-view.delete-warning'),
 *          type: 'is-danger',
 *          confirmText: this.$t('common.delete')
 *        }) && this.deleteProject()
 *
 *   2. mode: 'undo'   (preferred for reversible ops — archive, remove from list)
 *      Calls `perform()` immediately, then shows a snackbar with an Undo
 *      button. If the user clicks Undo, `revert()` runs. After `timeout`ms
 *      the action is committed (optional `commit()` callback runs to flush
 *      any deferred persistence).
 *
 *        this.$omxConfirm({
 *          mode: 'undo',
 *          message: this.$t('projects-view.archived-toast'),
 *          perform: () => this.archiveLocally(id),
 *          revert:  () => this.unarchiveLocally(id),
 *          commit:  () => this.persistArchive(id),
 *        })
 *
 * --- $omxAlert ---
 *
 *   await this.$omxAlert({
 *     title: this.$t('project-details-view.export-ready-title'),
 *     message: result.data.url,
 *     type: 'is-info'
 *   });
 *
 * --- $omxPrompt ---
 *
 *   const value = await this.$omxPrompt({
 *     message: this.$t('project-details-view.save-as-template-title'),
 *     inputAttrs: { placeholder: this.$t('project-details-view.template-code-placeholder') },
 *     confirmText: this.$t('common.save')
 *   });
 *   if (value !== null) save(value);   // null when cancelled
 */

function openConfirm(buefy, i18n, opts = {}) {
  const mode = opts.mode || "modal";

  if (mode === "undo") {
    let undone = false;
    const performFn = opts.perform || (() => {});
    const revertFn  = opts.revert  || (() => {});
    const commitFn  = opts.commit  || (() => {});

    // optimistic apply
    performFn();

    return new Promise((resolve) => {
      buefy.snackbar.open({
        message: opts.message || (i18n && i18n.t("common.action-applied")) || "Done",
        type: opts.type || "is-dark",
        position: opts.position || "is-bottom",
        actionText: opts.undoText || (i18n && i18n.t("common.undo")) || "Undo",
        indefinite: false,
        duration: opts.timeout || 6000,
        queue: false,
        onAction: () => {
          undone = true;
          revertFn();
          resolve({ undone: true });
        }
      });

      // when snackbar dismisses without action, commit
      setTimeout(() => {
        if (!undone) {
          commitFn();
          resolve({ undone: false });
        }
      }, (opts.timeout || 6000) + 50);
    });
  }

  // modal mode
  return new Promise((resolve) => {
    buefy.dialog.confirm({
      title: opts.title || (i18n && i18n.t("common.confirm-title")) || "Are you sure?",
      message: opts.message || "",
      type: opts.type || "is-danger",
      hasIcon: true,
      icon: opts.icon || "alert-circle-outline",
      confirmText: opts.confirmText || (i18n && i18n.t("common.confirm")) || "Confirm",
      cancelText: opts.cancelText || (i18n && i18n.t("common.cancel")) || "Cancel",
      onConfirm: () => resolve(true),
      onCancel: () => resolve(false)
    });
  });
}

function openAlert(buefy, i18n, opts = {}) {
  return new Promise((resolve) => {
    buefy.dialog.alert({
      title: opts.title || "",
      message: opts.message || "",
      type: opts.type || "is-info",
      hasIcon: opts.hasIcon !== false,
      icon: opts.icon || defaultIconForType(opts.type || "is-info"),
      confirmText: opts.confirmText || (i18n && i18n.t("common.confirm")) || "OK",
      onConfirm: () => resolve(true)
    });
  });
}

function openPrompt(buefy, i18n, opts = {}) {
  return new Promise((resolve) => {
    buefy.dialog.prompt({
      title: opts.title || "",
      message: opts.message || "",
      type: opts.type || "is-primary",
      inputAttrs: Object.assign(
          { type: "text", placeholder: "" },
          opts.inputAttrs || {}
      ),
      confirmText: opts.confirmText || (i18n && i18n.t("common.confirm")) || "OK",
      cancelText: opts.cancelText || (i18n && i18n.t("common.cancel")) || "Cancel",
      trapFocus: opts.trapFocus !== false,
      closeOnConfirm: opts.closeOnConfirm !== false,
      onConfirm: (value, dlg) => resolve({ value, close: dlg && dlg.close }),
      onCancel: () => resolve(null)
    });
  });
}

function defaultIconForType(type) {
  switch (type) {
    case "is-danger":  return "alert-circle-outline";
    case "is-warning": return "alert";
    case "is-success": return "check-circle-outline";
    case "is-info":    return "information-outline";
    default:           return "information-outline";
  }
}

Vue.mixin({
  methods: {
    $omxConfirm(opts) {
      return openConfirm(this.$buefy, this.$i18n, opts);
    },
    $omxAlert(opts) {
      return openAlert(this.$buefy, this.$i18n, opts);
    },
    $omxPrompt(opts) {
      return openPrompt(this.$buefy, this.$i18n, opts);
    }
  }
});

export default openConfirm;
